import { useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { Tooltip } from 'bootstrap'

/**
 * InfoTooltip Component
 *
 * A reusable tooltip component that displays informational help text
 * when hovering over an icon. Uses Bootstrap tooltips for consistent styling.
 *
 * @param {Object} props
 * @param {string} props.content - The tooltip content to display
 * @param {string} [props.placement='top'] - Tooltip placement (top, bottom, left, right)
 * @param {string} [props.className=''] - Additional CSS classes
 */
function InfoTooltip({ content, placement = 'top', className = '' }) {
  const tooltipRef = useRef(null)
  const tooltipInstanceRef = useRef(null)

  useEffect(() => {
    if (!tooltipRef.current || !content) return

    // Initialize Bootstrap tooltip
    tooltipInstanceRef.current = new Tooltip(tooltipRef.current, {
      title: content,
      placement: placement,
      trigger: 'hover focus',
      html: false,
    })

    // Cleanup function to dispose tooltip on unmount
    return () => {
      if (tooltipInstanceRef.current) {
        tooltipInstanceRef.current.dispose()
        tooltipInstanceRef.current = null
      }
    }
  }, [content, placement])

  return (
    <span
      ref={tooltipRef}
      className={`info-tooltip ${className}`}
      style={{
        cursor: 'help',
        display: 'inline-flex',
        alignItems: 'center',
        marginLeft: '4px',
        color: '#6c757d',
      }}
      tabIndex="0"
      aria-label={content}
    >
      <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="currentColor"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
        <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z" />
      </svg>
    </span>
  )
}

InfoTooltip.propTypes = {
  content: PropTypes.string.isRequired,
  placement: PropTypes.oneOf(['top', 'bottom', 'left', 'right']),
  className: PropTypes.string,
}

export default InfoTooltip
